import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Calendar, Users, Mail, Loader2, AlertCircle, Home, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxPollAttempts = 10;

  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollAttempts >= maxPollAttempts) {
      setError(language === 'es' 
        ? 'Tiempo de espera agotado. Por favor revise su correo para confirmacion.' 
        : 'Timed out checking payment status. Please check your email for confirmation.'
      );
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/payments/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setBooking(response.data.booking);
        setLoading(false);
      } else if (response.data.status === 'expired') {
        setError(language === 'es' 
          ? 'La sesion de pago ha expirado. Por favor intente de nuevo.' 
          : 'Payment session has expired. Please try again.'
        );
        setLoading(false);
      } else {
        // Continue polling
        setPollAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (err) {
      console.error('Error polling payment status:', err);
      setPollAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const texts = {
    en: {
      processing: 'Processing your booking...',
      success: 'Booking Confirmed!',
      thankYou: 'Thank you for your booking. You will receive a confirmation email shortly.',
      bookingDetails: 'Booking Details',
      trip: 'Trip',
      date: 'Date',
      travelers: 'Travelers',
      amountPaid: 'Amount Paid',
      balanceDue: 'Balance Due',
      dueBy: 'Due by',
      bookingRef: 'Booking Reference',
      status: 'Status',
      depositPaid: 'Deposit Paid',
      paidInFull: 'Paid in Full',
      whatNext: 'What happens next?',
      step1: 'Check your email for booking confirmation',
      step2: 'Our team will reach out with trip details',
      step3: 'Complete remaining balance before due date',
      backHome: 'Back to Home',
      viewTrips: 'Explore More Trips',
      error: 'Something went wrong'
    },
    es: {
      processing: 'Procesando tu reserva...',
      success: 'Reserva Confirmada!',
      thankYou: 'Gracias por tu reserva. Recibiras un correo de confirmacion pronto.',
      bookingDetails: 'Detalles de la Reserva',
      trip: 'Viaje',
      date: 'Fecha',
      travelers: 'Viajeros',
      amountPaid: 'Monto Pagado',
      balanceDue: 'Saldo Pendiente',
      dueBy: 'Vence el',
      bookingRef: 'Referencia de Reserva',
      status: 'Estado',
      depositPaid: 'Deposito Pagado',
      paidInFull: 'Pagado Completo',
      whatNext: 'Que sigue?',
      step1: 'Revisa tu correo para la confirmacion',
      step2: 'Nuestro equipo te contactara con los detalles',
      step3: 'Completa el saldo restante antes de la fecha limite',
      backHome: 'Volver al Inicio',
      viewTrips: 'Explorar Mas Viajes',
      error: 'Algo salio mal'
    }
  };

  const t = texts[language] || texts.en;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="w-16 h-16 text-[#84bc00] animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t.processing}</h1>
          <p className="text-gray-500">Please wait while we confirm your payment...</p>
          <div className="mt-4 flex justify-center gap-1">
            {[...Array(maxPollAttempts)].map((_, i) => (
              <div 
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i < pollAttempts ? 'bg-[#84bc00]' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t.error}</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-[#84bc00] hover:bg-[#6a9600]">
              <Home className="w-4 h-4 mr-2" />
              {t.backHome}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Success Header */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center mb-6">
          <div className="w-20 h-20 bg-[#84bc00] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-2">{t.success}</h1>
          <p className="text-gray-500">{t.thankYou}</p>
        </div>

        {/* Booking Details */}
        {booking && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#84bc00]" />
              {t.bookingDetails}
            </h2>

            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-500">{t.bookingRef}</span>
                <span className="font-mono font-bold text-[#84bc00]">{booking.booking_id}</span>
              </div>

              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-500">{t.trip}</span>
                <span className="font-bold text-gray-900">{booking.trip_name}</span>
              </div>

              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-500 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {t.date}
                </span>
                <span className="font-medium">{formatDate(booking.trip_date)}</span>
              </div>

              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-500 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {t.travelers}
                </span>
                <span className="font-medium">{booking.travelers}</span>
              </div>

              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-500">{t.amountPaid}</span>
                <span className="font-bold text-[#84bc00] text-lg">
                  ${booking.amount_paid?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                </span>
              </div>

              {booking.balance_due > 0 && (
                <>
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-gray-500">{t.balanceDue}</span>
                    <span className="font-bold text-orange-500 text-lg">
                      ${booking.balance_due?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b">
                    <span className="text-gray-500">{t.dueBy}</span>
                    <span className="font-medium text-orange-500">{formatDate(booking.balance_due_date)}</span>
                  </div>
                </>
              )}

              <div className="flex justify-between items-center py-3">
                <span className="text-gray-500">{t.status}</span>
                <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                  booking.status === 'paid_in_full' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {booking.status === 'paid_in_full' ? t.paidInFull : t.depositPaid}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* What's Next */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">{t.whatNext}</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-[#84bc00] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                1
              </div>
              <div>
                <p className="text-gray-700 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  {t.step1}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-[#84bc00] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                2
              </div>
              <p className="text-gray-700">{t.step2}</p>
            </div>
            {booking?.balance_due > 0 && (
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#84bc00] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  3
                </div>
                <p className="text-gray-700">{t.step3}</p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Link to="/" className="flex-1">
            <Button variant="outline" className="w-full py-6">
              <Home className="w-4 h-4 mr-2" />
              {t.backHome}
            </Button>
          </Link>
          <Link to="/trips" className="flex-1">
            <Button className="w-full bg-[#84bc00] hover:bg-[#6a9600] py-6">
              {t.viewTrips}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccess;
